import React from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, Wand2, ArrowRight } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert'
import { Button } from '../../ui/button'
import {
  SetupStepId,
  SETUP_STEPS_BY_ID,
  setupStepHref,
  wizardHref,
} from './eventSetupStatus'

interface SetupPrerequisiteAlertProps {
  eventId: number | string
  /** Steps that still need doing, in dependency order. */
  missing: SetupStepId[]
  /** What the user was trying to do, e.g. "create a shift". */
  action: string
  /** Hide the "Setup Wizard" shortcut when the surface already offers one. */
  showWizardLink?: boolean
  className?: string
}

const joinLabels = (labels: string[]): string => {
  if (labels.length === 0) return ''
  if (labels.length === 1) return labels[0]
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
}

/**
 * Names the actual blocker instead of letting the user hit an empty dropdown
 * or a silent no-op, and links straight to the step that fixes it.
 */
export const SetupPrerequisiteAlert: React.FC<SetupPrerequisiteAlertProps> = ({
  eventId,
  missing,
  action,
  showWizardLink = true,
  className,
}) => {
  if (missing.length === 0) return null

  const steps = missing.map((id) => SETUP_STEPS_BY_ID[id]).filter(Boolean)

  return (
    <Alert className={className}>
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>Setup needed before you can {action}</AlertTitle>
      <AlertDescription className="space-y-3">
        <p className="text-muted-foreground">
          This event still needs {joinLabels(steps.map((s) => s.label.toLowerCase()))}
          {steps.length === 1 && steps[0].consequence
            ? ` — ${steps[0].consequence}.`
            : '.'}
        </p>
        <div className="flex flex-wrap gap-2">
          {steps.map((step) => (
            <Button key={step.id} asChild size="sm" variant="outline">
              <Link to={setupStepHref(step.id, eventId)}>
                Set up {step.label.toLowerCase()}
                <ArrowRight className="ml-2 h-3.5 w-3.5" />
              </Link>
            </Button>
          ))}
          {showWizardLink && (
            <Button asChild size="sm" variant="ghost">
              <Link to={wizardHref(eventId)}>
                <Wand2 className="mr-2 h-3.5 w-3.5" />
                Setup Wizard
              </Link>
            </Button>
          )}
        </div>
      </AlertDescription>
    </Alert>
  )
}
